import { useState, useEffect } from 'react';
import { Phone, TrendingUp, Clock, DollarSign, AlertTriangle, Award } from 'lucide-react';
import { getTwilioCallAnalytics, getDateRange } from '../lib/twilio-call-analytics';
import { getFlashReportGenerator } from '../lib/flash-report-generator';

export default function CallPerformanceDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<string>('Hales AC');
  const [dateRange, setDateRange] = useState<number>(7);
  const [metrics, setMetrics] = useState<any>(null);
  const [flashReport, setFlashReport] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [selectedCompany, dateRange]);

  async function loadData() {
    setLoading(true);
    try {
      const analytics = getTwilioCallAnalytics();
      const range = getDateRange(dateRange);
      
      // Load call metrics
      const metricsData = await analytics.getCallMetrics({
        companyName: selectedCompany,
        startDate: range.startDate,
        endDate: range.endDate,
      });
      
      // Load today's flash report
      const reportGen = getFlashReportGenerator();
      const report = await reportGen.getCachedReport(selectedCompany);
      
      setMetrics(metricsData);
      setFlashReport(report);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Call Performance Dashboard</h1>
          <p className="text-gray-600 mt-1">Real-time call tracking and speed-to-lead metrics</p>
        </div>
        
        <div className="flex gap-4">
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="Hales AC">Hales AC</option>
            <option value="Climate Control Services">Climate Control Services</option>
            <option value="Abraham AC">Abraham AC</option>
          </select>
          
          <select
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Calls"
          value={metrics?.total_calls || 0}
          icon={Phone}
          trend="+12%"
          trendUp={true}
        />
        
        <MetricCard
          title="Answer Rate"
          value={`${(metrics?.answer_rate || 0).toFixed(1)}%`}
          icon={TrendingUp}
          trend="+5.2%"
          trendUp={true}
          target="Target: 85%"
        />
        
        <MetricCard
          title="Speed-to-Lead Score"
          value={flashReport?.speed_to_lead_score || 0}
          suffix="/100"
          icon={Clock}
          trend="+8 pts"
          trendUp={true}
        />
        
        <MetricCard
          title="Booking Rate"
          value={`${(flashReport?.booking_rate_percent || 0).toFixed(1)}%`}
          icon={Award}
          trend="+3.5%"
          trendUp={true}
          target="Goal: 60%"
        />
      </div>

      {/* Speed-to-Lead Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Speed-to-Lead Performance</h2>
        
        <div className="grid grid-cols-4 gap-4 mb-6">
          <SpeedBadge
            label="Excellent"
            sublabel="<60 seconds"
            count={metrics?.excellent_responses || 0}
            color="green"
          />
          <SpeedBadge
            label="Good"
            sublabel="1-5 minutes"
            count={metrics?.good_responses || 0}
            color="blue"
          />
          <SpeedBadge
            label="Poor"
            sublabel=">5 minutes"
            count={metrics?.poor_responses || 0}
            color="yellow"
          />
          <SpeedBadge
            label="Failed"
            sublabel="No response"
            count={metrics?.failed_responses || 0}
            color="red"
          />
        </div>

        {/* Progress Bar */}
        <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
          {metrics && (
            <>
              <div
                className="absolute h-full bg-green-500"
                style={{ width: `${(metrics.excellent_responses / metrics.total_calls) * 100}%` }}
              ></div>
              <div
                className="absolute h-full bg-blue-500"
                style={{
                  left: `${(metrics.excellent_responses / metrics.total_calls) * 100}%`,
                  width: `${(metrics.good_responses / metrics.total_calls) * 100}%`,
                }}
              ></div>
              <div
                className="absolute h-full bg-yellow-500"
                style={{
                  left: `${((metrics.excellent_responses + metrics.good_responses) / metrics.total_calls) * 100}%`,
                  width: `${(metrics.poor_responses / metrics.total_calls) * 100}%`,
                }}
              ></div>
              <div
                className="absolute h-full bg-red-500"
                style={{
                  left: `${((metrics.excellent_responses + metrics.good_responses + metrics.poor_responses) / metrics.total_calls) * 100}%`,
                  width: `${(metrics.failed_responses / metrics.total_calls) * 100}%`,
                }}
              ></div>
            </>
          )}
        </div>
      </div>

      {/* Revenue & ROI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Revenue Metrics</h2>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Total Booking Value</span>
              <span className="text-xl font-bold text-green-600">
                ${(metrics?.total_booking_value || 0).toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Average Job Value</span>
              <span className="text-lg font-semibold">
                ${(metrics?.avg_booking_value || 0).toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Calls to Bookings</span>
              <span className="text-lg font-semibold">
                {flashReport?.calls_to_bookings || 0}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">Booking Rate</span>
              <span className="text-lg font-semibold">
                {(flashReport?.booking_rate_percent || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Cost & ROI</h2>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Total Ad Spend</span>
              <span className="text-xl font-bold text-red-600">
                ${(flashReport?.total_ad_spend || 0).toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Cost Per Call</span>
              <span className="text-lg font-semibold">
                ${(flashReport?.avg_cost_per_call || 0).toFixed(2)}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Cost Per Booked Job</span>
              <span className="text-lg font-semibold text-blue-600">
                ${(flashReport?.cost_per_booked_job || 0).toFixed(2)}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">ROI</span>
              <span className={`text-xl font-bold ${(flashReport?.roi_percent || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(flashReport?.roi_percent || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance by Source */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Performance by Marketing Source</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calls
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ROI
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.keys(metrics?.calls_by_source || {}).map((source) => {
                const calls = metrics.calls_by_source[source];
                const bookings = metrics.bookings_by_source[source] || 0;
                const rate = calls > 0 ? (bookings / calls) * 100 : 0;
                const revenue = flashReport?.revenue_by_source?.[source] || 0;
                const roi = flashReport?.roi_by_source?.[source] || 0;
                
                return (
                  <tr key={source}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900 capitalize">{source}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{calls}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{bookings}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`font-medium ${rate >= 50 ? 'text-green-600' : rate >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      ${revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`font-medium ${roi > 100 ? 'text-green-600' : roi > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {roi.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Helper Components
function MetricCard({ title, value, suffix, icon: Icon, trend, trendUp, target }: any) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-600">{title}</span>
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900">
          {value}{suffix}
        </span>
      </div>
      
      {trend && (
        <div className="mt-2 flex items-center gap-2">
          <span className={`text-sm font-medium ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
            {trend}
          </span>
          <span className="text-xs text-gray-500">vs last period</span>
        </div>
      )}
      
      {target && (
        <div className="mt-1 text-xs text-gray-500">{target}</div>
      )}
    </div>
  );
}

function SpeedBadge({ label, sublabel, count, color }: any) {
  const colors = {
    green: 'bg-green-100 text-green-800 border-green-300',
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    red: 'bg-red-100 text-red-800 border-red-300',
  };

  return (
    <div className={`${colors[color as keyof typeof colors]} border-2 rounded-lg p-4 text-center`}>
      <div className="text-3xl font-bold mb-1">{count}</div>
      <div className="text-sm font-semibold">{label}</div>
      <div className="text-xs opacity-75">{sublabel}</div>
    </div>
  );
}
